import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, Loader2, Check, Building2 } from "lucide-react";
import { VisitCadenceEditor, VisitCadenceItem } from "./VisitCadenceEditor";

interface ClaimTrialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteOrgId: string;
  trialSiteId?: string; // Direct trial_site_id for proper linking
  croOrgId?: string;
  onTrialClaimed?: (trialId: string, croTrialId: string) => void;
}

interface GlobalTrial {
  id: string;
  nct_id: string | null;
  title: string;
  phase: string | null;
  status: string;
  sponsor_name: string | null;
  condition: string | null;
  enrollment_target: number | null;
}

// Visit types must match database enum: screening, baseline, week_4, week_8, month_6, end_of_study, unscheduled, adverse_event
const DEFAULT_VISIT_CADENCE: VisitCadenceItem[] = [
  { id: '1', name: 'Screening', type: 'screening', days_from_enrollment: -14, window_before: 0, window_after: 7, billable: true },
  { id: '2', name: 'Baseline', type: 'baseline', days_from_enrollment: 0, window_before: 0, window_after: 3, billable: true },
  { id: '3', name: 'Week 4', type: 'week_4', days_from_enrollment: 28, window_before: 3, window_after: 3, billable: true },
  { id: '4', name: 'Week 8', type: 'week_8', days_from_enrollment: 56, window_before: 3, window_after: 3, billable: true },
  { id: '5', name: 'Month 6', type: 'month_6', days_from_enrollment: 168, window_before: 7, window_after: 7, billable: true },
  { id: '6', name: 'End of Study', type: 'end_of_study', days_from_enrollment: 252, window_before: 7, window_after: 7, billable: true },
];

export function ClaimTrialDialog({ 
  open, 
  onOpenChange, 
  siteOrgId,
  trialSiteId,
  croOrgId,
  onTrialClaimed 
}: ClaimTrialDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTrial, setSelectedTrial] = useState<GlobalTrial | null>(null);
  const [step, setStep] = useState<'search' | 'cadence'>('search');
  const [visitCadence, setVisitCadence] = useState<VisitCadenceItem[]>(DEFAULT_VISIT_CADENCE);
  const [enrollmentTarget, setEnrollmentTarget] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Search global trials
  const { data: searchResults = [], isLoading: isSearching } = useQuery({
    queryKey: ['global-trials-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 3) return [];
      
      const { data, error } = await supabase
        .from('trials')
        .select('id, nct_id, title, phase, status, sponsor_name, condition, enrollment_target')
        .or(`title.ilike.%${searchQuery}%,nct_id.ilike.%${searchQuery}%,condition.ilike.%${searchQuery}%`)
        .in('status', ['Recruiting', 'Not yet recruiting', 'Active, not recruiting', 'recruiting', 'active'])
        .limit(20);

      if (error) throw error;
      return (data || []) as GlobalTrial[];
    },
    enabled: searchQuery.length >= 3,
  });

  // Claim trial mutation
  const claimTrialMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTrial) throw new Error("No trial selected");

      // Create cro_trials record
      const { data: croTrial, error: croTrialError } = await supabase
        .from('cro_trials')
        .insert({
          trial_id: selectedTrial.id,
          title: selectedTrial.title,
          nct_id: selectedTrial.nct_id,
          phase: selectedTrial.phase,
          status: 'enrolling',
          target_enrollment: selectedTrial.enrollment_target,
          cro_organization_id: croOrgId || siteOrgId,
        })
        .select()
        .single();

      if (croTrialError) throw croTrialError;

      // Link to site if siteOrgId provided
      // Include trial_site_id for proper linkage to cro_site_assignments
      const { error: siteError } = await supabase
        .from('cro_trial_sites')
        .insert({
          cro_trial_id: croTrial.id,
          site_organization_id: siteOrgId,
          trial_site_id: trialSiteId || null, // Link to trial_sites table
          site_name: 'Trial Site',
          site_status: 'activated',
          enrollment_target: enrollmentTarget ? parseInt(enrollmentTarget) : null,
        });

      if (siteError) throw siteError;

      // Create or update trial_protocols with visit_schedule
      const visitScheduleJson = {
        visits: visitCadence.map((v, idx) => ({
          name: v.name,
          type: v.type,
          visit_number: idx + 1,
          days_from_enrollment: v.days_from_enrollment,
          window_before: v.window_before,
          window_after: v.window_after,
          billable: v.billable,
        }))
      };

      // Check if protocol exists
      const { data: existingProtocol } = await supabase
        .from('trial_protocols')
        .select('id')
        .eq('trial_id', selectedTrial.id)
        .single();

      if (existingProtocol) {
        await supabase
          .from('trial_protocols')
          .update({ visit_schedule: visitScheduleJson })
          .eq('id', existingProtocol.id);
      } else {
        await supabase
          .from('trial_protocols')
          .insert({
            trial_id: selectedTrial.id,
            visit_schedule: visitScheduleJson,
            inclusion_criteria: {},
            exclusion_criteria: {},
            primary_endpoints: {},
            secondary_endpoints: {},
            safety_considerations: {},
            eligibility_requirements: {},
          });
      }

      return { trialId: selectedTrial.id, croTrialId: croTrial.id };
    },
    onSuccess: (data) => {
      toast({
        title: "Trial claimed successfully",
        description: `${selectedTrial?.title} has been added to your active trials with ${visitCadence.length} scheduled visits.`,
      });
      queryClient.invalidateQueries({ queryKey: ['active-trials'] });
      queryClient.invalidateQueries({ queryKey: ['cro-trials'] });
      onTrialClaimed?.(data.trialId, data.croTrialId);
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to claim trial",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setSearchQuery("");
    setSelectedTrial(null);
    setStep('search');
    setVisitCadence(DEFAULT_VISIT_CADENCE);
    setEnrollmentTarget("");
    onOpenChange(false);
  };

  const handleSelectTrial = (trial: GlobalTrial) => {
    setSelectedTrial(trial);
    setStep('cadence');
  };

  const handleBack = () => {
    setStep('search');
    setSelectedTrial(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {step === 'search' ? 'Claim Existing Trial' : 'Configure Visit Schedule'}
          </DialogTitle>
          <DialogDescription>
            {step === 'search' 
              ? 'Search for a trial from the global database to add to your site'
              : `Configure the visit cadence for ${selectedTrial?.title}`
            }
          </DialogDescription>
        </DialogHeader>

        {step === 'search' ? (
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, NCT ID, or condition..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Results */}
            <ScrollArea className="flex-1 min-h-0">
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : searchQuery.length < 3 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Enter at least 3 characters to search
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No trials found matching "{searchQuery}"
                </div>
              ) : (
                <div className="space-y-2 pr-4">
                  {searchResults.map((trial) => (
                    <div
                      key={trial.id}
                      className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => handleSelectTrial(trial)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {trial.nct_id && (
                              <Badge variant="outline" className="font-mono text-xs">
                                {trial.nct_id}
                              </Badge>
                            )}
                            {trial.phase && (
                              <Badge variant="secondary">{trial.phase}</Badge>
                            )}
                            <Badge variant={trial.status?.toLowerCase().includes('recruiting') ? 'default' : 'secondary'}>
                              {trial.status}
                            </Badge>
                          </div>
                          <h4 className="font-medium text-sm line-clamp-2">{trial.title}</h4>
                          {trial.sponsor_name && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <Building2 className="h-3 w-3" />
                              {trial.sponsor_name}
                            </p>
                          )}
                          {trial.condition && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                              {trial.condition}
                            </p>
                          )}
                        </div>
                        <Button size="sm" variant="outline">
                          Select
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-auto">
            {/* Selected Trial Summary */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                {selectedTrial?.nct_id && (
                  <Badge variant="outline" className="font-mono text-xs">
                    {selectedTrial.nct_id}
                  </Badge>
                )}
                <Check className="h-4 w-4 text-green-500" />
              </div>
              <p className="font-medium text-sm">{selectedTrial?.title}</p>
            </div>

            {/* Site Enrollment Target */}
            <div className="space-y-2">
              <Label htmlFor="enrollment-target">Site Enrollment Target</Label>
              <Input
                id="enrollment-target"
                type="number"
                value={enrollmentTarget}
                onChange={(e) => setEnrollmentTarget(e.target.value)}
                placeholder="e.g., 25"
              />
            </div>

            {/* Visit Cadence Editor */}
            <VisitCadenceEditor
              visits={visitCadence}
              onChange={setVisitCadence}
            />
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-between gap-3 pt-4 border-t">
          {step === 'cadence' && (
            <Button variant="outline" onClick={handleBack}>
              Back to Search
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {step === 'cadence' && (
            <Button 
              onClick={() => claimTrialMutation.mutate()}
              disabled={claimTrialMutation.isPending}
            >
              {claimTrialMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Claiming...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Claim Trial
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
